/**
 * 字幕版式纯逻辑(VID-D,2026-08-18 老板走查)—— 2026-09-09 从 features/video/model 抽到
 * shared:数字人成片重合成(DhComposePanel)要用同一套预览/换算,FSD features 互不 import。
 * 原路径留 re-export 壳,视频线导入零改动。三个档位类型一并落这里(videoService re-export)。
 */
export type SubtitleFontScale = "small" | "medium" | "large";
export type SubtitlePosition = "top" | "center" | "bottom";
export type SubtitleOutline = "none" | "thin" | "thick";

/**
 * 成片编辑的「字幕版式」纯逻辑(VID-D,2026-08-18 老板走查)。
 *
 * ★这些档位的**语义与数值都由引擎定**(ai `srt_utils.build_ass`):
 *   大小 small/medium/large = 自动字号 ×0.82/1.0/1.28;位置 = ASS 三档对齐;
 *   描边 none/thin/thick = 0/1/2 倍基准粗细(thin 即此前写死值)。
 *   这里只做**回显与预览**,不重算引擎口径 —— 两处各算一套就会漂。
 */
export interface SubtitleStyle {
  fontScale: SubtitleFontScale;
  position: SubtitlePosition;
  outline: SubtitleOutline;
  /** 竖向锚点 0..1(字幕行中心占画面高的比例);null = 用 position 三档。 */
  anchorY: number | null;
  /** 字幕最大宽度占画面宽的比例 0.3..1;null = 引擎默认。 */
  widthPct: number | null;
}

type Payload = Record<string, unknown> | undefined;

export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  fontScale: "medium",
  position: "bottom",
  outline: "thin",
  anchorY: null,
  widthPct: null,
};

/** 位置三档 → 预览用的竖向比例(与引擎 MarginV 口径同向:底部不贴边、留出手机安全区)。 */
const POSITION_PREVIEW_Y: Record<SubtitlePosition, number> = {
  top: 0.1,
  center: 0.5,
  bottom: 0.9,
};

const FONT_SCALES: readonly SubtitleFontScale[] = ["small", "medium", "large"];
const POSITIONS: readonly SubtitlePosition[] = ["top", "center", "bottom"];
const OUTLINES: readonly SubtitleOutline[] = ["none", "thin", "thick"];

function pick<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

function num(v: unknown, lo: number, hi: number): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return Math.min(hi, Math.max(lo, v));
}

/** 回显已存版式;没存过 → 默认(与引擎缺省一致,老片打开不变样)。 */
export function readSubtitleStyle(payload: Payload): SubtitleStyle {
  return {
    fontScale: pick(payload?.subtitleFontScale, FONT_SCALES, "medium"),
    position: pick(payload?.subtitlePosition, POSITIONS, "bottom"),
    outline: pick(payload?.subtitleOutline, OUTLINES, "thin"),
    anchorY: num(payload?.subtitleAnchorY, 0, 1),
    widthPct: num(payload?.subtitleWidthPct, 0.3, 1),
  };
}

/**
 * 预览用的竖向位置:拖过就用拖出来的,没拖过按三档。
 * ★与引擎一致地**钳进安全区**:字幕每句长短不同,贴边会有半行被切在画外。
 */
export function previewAnchorY(style: SubtitleStyle): number {
  const raw = style.anchorY ?? POSITION_PREVIEW_Y[style.position];
  return Math.min(0.92, Math.max(0.08, raw));
}

/** 拖动落点 → 新版式:竖向锚点自由,**横向不动**(横向恒居中是刻意的,见 videoService 注释)。 */
export function applySubtitleDrag(style: SubtitleStyle, anchorY: number): SubtitleStyle {
  return { ...style, anchorY: Math.min(1, Math.max(0, anchorY)) };
}

/** 宽度手柄落点 → 新版式(0.3~1 钳制,太窄会逐字换行成一根竖条)。 */
export function applySubtitleWidth(style: SubtitleStyle, widthPct: number): SubtitleStyle {
  return { ...style, widthPct: Math.min(1, Math.max(0.3, widthPct)) };
}

/** 回到三档位置:清掉拖出来的锚点(否则档位按钮点了没反应 —— 锚点优先级更高)。 */
export function applySubtitlePosition(
  style: SubtitleStyle,
  position: SubtitlePosition
): SubtitleStyle {
  return { ...style, position, anchorY: null };
}

/** 两个版式是否一致 —— 面板判「有无未应用改动」。 */
export function subtitleStyleEqual(a: SubtitleStyle, b: SubtitleStyle): boolean {
  return (
    a.fontScale === b.fontScale &&
    a.position === b.position &&
    a.outline === b.outline &&
    a.anchorY === b.anchorY &&
    a.widthPct === b.widthPct
  );
}

/** 版式 → 重合成参数(始终全量发:后端非空才覆盖,发全量才能把用户的"改回默认"也传达到)。 */
export function subtitleComposeOptions(style: SubtitleStyle) {
  return {
    subtitleFontScale: style.fontScale,
    subtitlePosition: style.position,
    subtitleOutline: style.outline,
    subtitleAnchorY: style.anchorY,
    subtitleWidthPct: style.widthPct,
  };
}

/** 预览字号:按画布高度 × 引擎口径(h/30 × 档位),让所见接近所得。 */
export function previewFontPx(style: SubtitleStyle, canvasHeight: number): number {
  const scale = style.fontScale === "small" ? 0.82 : style.fontScale === "large" ? 1.28 : 1;
  return Math.max(9, Math.round((canvasHeight / 30) * scale));
}

/** 预览描边:none 不描,thin/thick 与引擎同向(1×/2× 基准)。 */
export function previewOutlinePx(style: SubtitleStyle, canvasHeight: number): number {
  if (style.outline === "none") return 0;
  const base = canvasHeight <= 400 ? 1 : 1.5;
  return style.outline === "thick" ? base * 2 : base;
}
